import { can } from "@bt/domain";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { QrCodeIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@/components/app/empty-state";
import { Page, PageHeader } from "@/components/app/page";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useModule } from "@/lib/modules";
import { useTRPC } from "@/lib/trpc";
import { BasketTab } from "./basket";
import { BlankTab } from "./blank";
import { PendingTab } from "./pending";

export const LABEL_TABS = ["basket", "pending", "blank"] as const;
export type LabelTab = (typeof LABEL_TABS)[number];

/**
 * Labels (ADR 0018 › Where labels show): the label basket with the print page,
 * the printed labels not yet confirmed on their shelf, and the blank ones.
 */
export function LabelsPage({ tab, onTab }: { tab: LabelTab; onTab: (tab: LabelTab) => void }) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const active = useModule("qrLabels");
  const { data: access } = useQuery(trpc.garages.access.queryOptions());
  const { data: basket } = useQuery({ ...trpc.labels.basket.queryOptions(), enabled: active });
  const { data: pending } = useQuery({ ...trpc.labels.pending.queryOptions(), enabled: active });
  const { data: blank } = useQuery({ ...trpc.labels.blank.queryOptions(), enabled: active });

  if (access && !active) {
    return (
      <Page>
        <PageHeader title={t("labels.title")} />
        <EmptyState
          icon={QrCodeIcon}
          title={t("labels.off")}
          body={t("labels.offBody")}
          action={
            can(access, "garage.manage") ? (
              <Button asChild variant="outline">
                <Link to="/settings/modules">{t("labels.switchOn")}</Link>
              </Button>
            ) : null
          }
        />
      </Page>
    );
  }

  const count = (n: number | undefined) =>
    n ? <span className="text-xs text-muted-foreground tabular">{n}</span> : null;
  return (
    <Page>
      <PageHeader title={t("labels.title")} subtitle={t("labels.subtitle")} />
      <Tabs value={tab} onValueChange={(value) => onTab(value as LabelTab)}>
        <TabsList>
          <TabsTrigger value="basket">
            {t("labels.basket")}
            {count(basket?.length)}
          </TabsTrigger>
          <TabsTrigger value="pending">
            {t("labels.pending")}
            {count(pending?.length)}
          </TabsTrigger>
          <TabsTrigger value="blank">
            {t("labels.blank")}
            {count(blank?.length)}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="basket">
          <BasketTab basket={basket} />
        </TabsContent>
        <TabsContent value="pending">
          <PendingTab labels={pending} />
        </TabsContent>
        <TabsContent value="blank">
          <BlankTab labels={blank} />
        </TabsContent>
      </Tabs>
    </Page>
  );
}
